<script setup lang="ts">
import type { CreateForecastScenarioInput } from '../internal/types.ts'
import { ActionButton, Dialog, StatusTag, TextField } from '@taler/ui'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [input: CreateForecastScenarioInput] }>()

const { t } = useI18n()

const open = ref(false)
const name = ref('')

function reset(): void {
  name.value = ''
}

function submit(): void {
  if (name.value.trim() === '') return
  emit('create', { name: name.value.trim() })
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) {
      reset()
      open.value = false
    }
  },
)
</script>

<template>
  <div>
    <ActionButton type="button" @click="open = true">
      {{ t('forecast.addScenario') }}
    </ActionButton>

    <Dialog :open="open" :label="t('forecast.addScenario')" @close="open = false">
      <form class="flex w-96 flex-col gap-4 p-[var(--space-block)]" @submit.prevent="submit">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('forecast.addScenario') }}
        </h2>
        <TextField v-model="name" :label="t('forecast.nameLabel')" maxlength="255" required autofocus />

        <StatusTag v-if="errorMessage" tone="danger">{{ errorMessage }}</StatusTag>

        <div class="flex gap-2">
          <ActionButton type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? t('forecast.saving') : t('forecast.save') }}
          </ActionButton>
          <ActionButton type="button" variant="secondary" @click="open = false">
            {{ t('forecast.cancel') }}
          </ActionButton>
        </div>
      </form>
    </Dialog>
  </div>
</template>
